// In Next.js, this file would be called: app/providers.jsx

'use client';

// We can not useState or useRef in a server component, which is why we are
// extracting this part out into it's own file with 'use client' on top
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import * as Sentry from 'utils/errorTracking';

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 5 * 60 * 1000, // 5 minutes - prevents duplicate API calls
        gcTime: 10 * 60 * 1000, // 10 minutes cache time
        refetchOnWindowFocus: false, // Prevents refetch when window gains focus
        refetchOnReconnect: false, // Prevents refetch on network reconnect
        retry: (failureCount, error: any) => {
          if (error?.status === 401 || error?.response?.status === 401) return false;
          return failureCount < 1;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error) => {
        Sentry.captureException(error, {
          tags: { errorType: 'react-query' },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        Sentry.captureException(error, {
          tags: { errorType: 'react-mutation' },
        });
      },
    }),
  });
};
let browserQueryClient: QueryClient | undefined;
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
};

const ReactQueryProvider = ({ children }: { children: React.ReactNode }) => {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

export default ReactQueryProvider;
